import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Card, Table, Button } from 'react-bootstrap';
import { buildClipboardHtml } from '../../../../utils/buildClipboardHtml';
import ChairReport from '../ChairReport/ChairReport';
import CommitteeBanner from '../../CommitteeBanner';
import useCommitteeHelpers from './hooks/useCommitteeHelpers';
import { devError } from '../../../../utils/devLog';

const CommitteeCard = ({
  committee,
  committeeMembers,
  reportStatus,
  isChairForThis,
  isAdmin,
  canDownloadPdf,
  editingCommitteeId,
  onRequestEdit,
  onDirtyChange,
  onReportSaved,
  showNotification,
}) => {
  const [isReportOpen, setIsReportOpen] = useState(false);
  const [copiedCommitteeId, setCopiedCommitteeId] = useState(false);

  const { formatCandidate, getCommitteeInfo, getLastColumnLabel } =
    useCommitteeHelpers();

  const { chairName, observers } = getCommitteeInfo(committee);
  const lastColLabel = getLastColumnLabel(committee);
  const committeeId = Number(committee.sectionalCommitteeId || committee.id);

  const handleCopyCommitteeData = async () => {
    try {
      const { plainText, htmlText } = buildClipboardHtml({
        committee,
        chairName,
        observers,
        lastColLabel,
        formatCandidate,
      });

      const clipboardItem = new ClipboardItem({
        'text/plain': new Blob([plainText], { type: 'text/plain' }),
        'text/html': new Blob([htmlText], { type: 'text/html' }),
      });

      await navigator.clipboard.write([clipboardItem]);
      setCopiedCommitteeId(true);
      setTimeout(() => setCopiedCommitteeId(false), 2000);
    } catch (error) {
      devError('Failed to copy committee data:', error);
      alert('Failed to copy data. Please try again.');
    }
  };

  const bannerActions = (
    <div className="d-flex gap-2">
      <Button
        variant="outline-primary"
        onClick={() => setIsReportOpen((prev) => !prev)}
        className="d-flex align-items-center btn-chairs-report"
      >
        <i className={`fas fa-chevron-${isReportOpen ? 'up' : 'down'} me-2`} />
        {isReportOpen ? 'Hide' : 'View'} Chair&#x2019;s Report
      </Button>
      <Button
        variant="outline-primary"
        onClick={handleCopyCommitteeData}
        className="d-flex align-items-center btn-copy-data"
      >
        <i
          className={`fas ${copiedCommitteeId ? 'fa-check' : 'fa-copy'} me-2`}
        />
        {copiedCommitteeId ? 'Copied!' : 'Copy Data'}
      </Button>
    </div>
  );

  const statusBadge = (
    <span
      className={`badge border border-${reportStatus.variant} text-${reportStatus.variant} bg-white`}
    >
      Report {reportStatus.label}
    </span>
  );

  return (
    <div className="committee-section">
      <CommitteeBanner
        name={committee.sectionalCommitteeName}
        badges={statusBadge}
        actions={bannerActions}
      />
      <Card className="card-two table-responsive">
        <Card.Body>
          <div className="committee-section-meta mb-3">
            <span>
              <strong>Chair:</strong> {chairName}
              <br />
              <strong>Observers:</strong> {observers}
            </span>
          </div>
          <Table responsive bordered hover className="mb-0">
            <colgroup>
              <col style={{ width: '5%' }} />
              <col style={{ width: '20%' }} />
              <col style={{ width: '10%' }} />
              <col style={{ width: '15%' }} />
              <col style={{ width: '20%' }} />
              <col style={{ width: '10%' }} />
              <col style={{ width: '20%' }} />
            </colgroup>
            <thead>
              <tr>
                <th>Rank</th>
                <th>Name</th>
                <th>Gender</th>
                <th>Country of Birth</th>
                <th>Institution</th>
                <th>{lastColLabel}</th>
                <th>Field of Expertise</th>
              </tr>
            </thead>
            <tbody>
              {committee.candidates.map((candidate, candIdx) => {
                const f = formatCandidate(candidate, committee.electionTypeId);
                return (
                  <tr key={candIdx}>
                    <td className="text-center">
                      <strong>{f.rank}</strong>
                    </td>
                    <td className="standard-cell-width">{f.name}</td>
                    <td className="standard-cell-width">{f.gender}</td>
                    <td className="standard-cell-width">{f.country}</td>
                    <td className="standard-cell-width">{f.institution}</td>
                    <td className="standard-cell-width">{f.stateOrCountry}</td>
                    <td className="standard-cell-width">{f.expertise}</td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
          {isReportOpen && (
            <ChairReport
              committee={committee}
              candidates={committee.candidates}
              committeeMembers={committeeMembers}
              chairName={chairName}
              isChair={isChairForThis}
              isAdmin={isAdmin}
              canDownloadPdf={canDownloadPdf}
              isReadOnly={!isChairForThis && !isAdmin}
              electionTypeId={committee.electionTypeId}
              formatCandidate={formatCandidate}
              showNotification={showNotification}
              onReportSaved={onReportSaved}
              isEditing={editingCommitteeId === committeeId}
              onRequestEdit={onRequestEdit}
              onDirtyChange={onDirtyChange}
            />
          )}
        </Card.Body>
      </Card>
    </div>
  );
};

CommitteeCard.propTypes = {
  committee: PropTypes.object.isRequired,
  committeeMembers: PropTypes.array.isRequired,
  reportStatus: PropTypes.shape({
    label: PropTypes.string,
    variant: PropTypes.string,
  }).isRequired,
  isChairForThis: PropTypes.bool.isRequired,
  isAdmin: PropTypes.bool.isRequired,
  canDownloadPdf: PropTypes.bool,
  editingCommitteeId: PropTypes.number,
  onRequestEdit: PropTypes.func.isRequired,
  onDirtyChange: PropTypes.func.isRequired,
  onReportSaved: PropTypes.func.isRequired,
  showNotification: PropTypes.func.isRequired,
};

CommitteeCard.defaultProps = {
  canDownloadPdf: false,
  editingCommitteeId: null,
};

export default CommitteeCard;
